/**
 * Wave 26.10.14 — Universal ShareButton.
 *
 * Drop-in popover that issues a short-lived signed share token and
 * presents Copy link / Chat / WhatsApp / Email / Download options.
 *
 * Props:
 *   kind:        "upload" | "library_report" | "engineering_session" | "jobcard" | "ticket" | "customer" | "datasheet"
 *   refId:       the underlying entity id
 *   title?:      caption used in WhatsApp/Email body and OG preview
 *   downloadUrl?: direct file URL (only for uploads — adds Download option)
 *   className?:  extra classes for the trigger button
 *   compact?:    boolean — if true, render only the icon
 */
import { useState, useRef, useEffect, useCallback } from "react";
import {
  Share2, Copy, MessageSquare, Phone, Mail, Download, Loader2, Check, X,
  FileUp,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "./ui/sonner";
import { shareFile, isFileShareSupported } from "../lib/shareFile";
import { useBranding } from "../context/TenantBrandingContext";

const PRETTY_KIND = {
  upload: "this file",
  library_report: "this report",
  engineering_session: "this session",
  jobcard: "this job card",
  ticket: "this ticket",
  customer: "this customer",
  datasheet: "this datasheet",
};

export default function ShareButton({
  kind, refId, title, downloadUrl, onPdfShare, className = "", compact = false, testId = "share-btn",
}) {
  const { branding } = useBranding();
  const brandName = branding?.app_name || "Rogue";
  const [open, setOpen] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [chatPickerOpen, setChatPickerOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const ensureLink = useCallback(async () => {
    if (link) return link;
    setIssuing(true);
    try {
      const { data } = await api.post("/share/links", { kind, ref_id: refId, ttl_days: 7, title });
      setLink(data);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate share link.");
      return null;
    } finally {
      setIssuing(false);
    }
  }, [link, kind, refId, title]);

  const captionFor = (l) => {
    const what = title || PRETTY_KIND[kind] || "this";
    return `${what}\n${l.url}`;
  };

  const onCopy = async () => {
    const l = await ensureLink();
    if (!l) return;
    try {
      await navigator.clipboard.writeText(l.url);
      setCopied(true);
      toast.success("Link copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: prompt
      // eslint-disable-next-line no-alert
      window.prompt("Copy this link:", l.url);
    }
  };

  const onWhatsApp = async () => {
    const l = await ensureLink();
    if (!l) return;
    const text = encodeURIComponent(captionFor(l));
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const onEmail = async () => {
    const l = await ensureLink();
    if (!l) return;
    const subject = encodeURIComponent(title || `Shared from ${brandName}`);
    const body = encodeURIComponent(captionFor(l));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setOpen(false);
  };

  const onChat = async () => {
    await ensureLink();
    setChatPickerOpen(true);
  };

  const onDownload = () => {
    if (!downloadUrl) return;
    // Use anchor with download attr; falls back to opening in new tab if
    // the server has Content-Disposition: inline.
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = title || "download";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setOpen(false);
  };

  // Iter 187 — share the RAW FILE BYTES (not a link) when possible. Uses
  // Web Share API on iOS/Android so the OS share sheet attaches the
  // actual file to WhatsApp / Email / Drive instead of a URL that lands
  // on a broken signed-link page. Falls back to a silent download + toast.
  const onShareFileDirect = async () => {
    if (!downloadUrl) return;
    setOpen(false);
    await shareFile({
      url: downloadUrl,
      filename: title || "share",
      title: title || PRETTY_KIND[kind] || `Shared from ${brandName}`,
    });
  };

  // Iter 231.2 — share-as-PDF for library reports. Caller provides an
  // async function that returns the PDF bytes; we wrap it in a File
  // and hand to the OS share sheet (or fall back to download).
  const [pdfBusy, setPdfBusy] = useState(false);
  const onShareAsPdf = async () => {
    if (!onPdfShare) return;
    setPdfBusy(true);
    try {
      const result = await onPdfShare();
      if (!result) return;
      const { blob, filename } = result;
      if (!blob) {
        toast.error("PDF generation failed.");
        return;
      }
      const file = new File([blob], filename || "report.pdf", { type: "application/pdf" });
      // Try native share with file first.
      if (
        typeof navigator !== "undefined"
        && typeof navigator.canShare === "function"
        && navigator.canShare({ files: [file] })
        && typeof navigator.share === "function"
      ) {
        try {
          await navigator.share({
            files: [file],
            title: title || filename || "Hidro report",
            text: title || filename || "Hidro report",
          });
          setOpen(false);
          return;
        } catch (err) {
          if (err?.name === "AbortError") {
            setOpen(false);
            return;
          }
          // fall through to download
        }
      }
      // Fallback — silent download + toast.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      toast.success("PDF downloaded", {
        description: "Drop it into WhatsApp, Email, Drive — whatever you like.",
        duration: 5000,
      });
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate PDF.");
    } finally {
      setPdfBusy(false);
    }
  };

  const trigger = compact ? (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
      className={`text-[#a0aabf] hover:text-[#1FB6C1] p-1 ${className}`}
      title="Share"
      aria-label="Share"
      data-testid={testId}
    >
      <Share2 className="w-4 h-4" />
    </button>
  ) : (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
      className={`hv-btn hv-btn-ghost text-xs ${className}`}
      data-testid={testId}
    >
      <Share2 className="w-3.5 h-3.5" /> Share
    </button>
  );

  return (
    <div className="relative inline-block" ref={ref}>
      {trigger}
      {open && (
        <div
          className="absolute right-0 mt-1 z-50 hv-card !p-1.5 shadow-xl border-white/15 min-w-[180px]"
          style={{ maxWidth: 'min(220px, calc(100vw - 1rem))' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="share-menu"
        >
          {downloadUrl && (
            <>
              <Item
                icon={FileUp}
                label="Share file directly"
                onClick={onShareFileDirect}
                testId="share-file-direct"
              />
              <div className="border-t border-white/5 my-0.5" />
            </>
          )}
          {onPdfShare && (
            <>
              <Item
                icon={FileUp}
                label="Share as PDF"
                onClick={onShareAsPdf}
                testId="share-as-pdf"
                busy={pdfBusy}
              />
              <div className="border-t border-white/5 my-0.5" />
            </>
          )}
          <Item icon={Copy} label={copied ? "Copied!" : "Copy link"} onClick={onCopy} testId="share-copy" busy={issuing && !link} />
          <Item icon={MessageSquare} label="Send via Chat" onClick={onChat} testId="share-chat" busy={issuing && !link} />
          <Item icon={Phone} label="WhatsApp (link)" onClick={onWhatsApp} testId="share-whatsapp" busy={issuing && !link} />
          <Item icon={Mail} label="Email (link)" onClick={onEmail} testId="share-email" busy={issuing && !link} />
          {downloadUrl && (
            <Item icon={Download} label="Download" onClick={onDownload} testId="share-download" />
          )}
          {link && (
            <div className="text-[9px] text-[#647087] px-2 py-1 mt-1 border-t border-white/10">
              Link expires {new Date(link.expires_at).toLocaleDateString()}
            </div>
          )}
        </div>
      )}
      {chatPickerOpen && link && (
        <ChatPickerModal
          link={link}
          caption={title || PRETTY_KIND[kind] || `Shared from ${brandName}`}
          onClose={() => { setChatPickerOpen(false); setOpen(false); }}
          onSent={() => { setChatPickerOpen(false); setOpen(false); toast.success("Sent to chat."); }}
        />
      )}
    </div>
  );
}

function Item({ icon: Icon, label, onClick, testId, busy }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-white/5 flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
      data-testid={testId}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" /> : <Icon className="w-3.5 h-3.5 shrink-0" />}
      <span className="truncate">{label}</span>
    </button>
  );
}

function ChatPickerModal({ link, caption, onClose, onSent }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/chats");
        const arr = Array.isArray(data) ? data : (data?.threads || []);
        if (alive) setThreads(arr);
      } catch (e) {
        toast.error("Couldn't load chats.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const send = async (thread) => {
    setBusy(true);
    try {
      const text = `${caption}\n${link.url}`;
      await api.post(`/chats/${thread.id}/messages`, { kind: "text", body: text });
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send to chat.");
    } finally { setBusy(false); }
  };

  const filtered = threads.filter((t) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return ((t.title || "") + (t.subtitle || "")).toLowerCase().includes(needle);
  });

  return (
    <div className="fixed inset-0 z-[210] bg-black/70 backdrop-blur flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-[#0e1015] border border-white/10 rounded-t-2xl sm:rounded-lg w-full sm:max-w-md p-4 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-chat-picker"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold">Send to chat</div>
          <button onClick={onClose} className="text-[#647087] hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search chat threads…"
          className="hv-input text-xs w-full mb-2"
          data-testid="share-chat-search"
        />
        {loading ? (
          <div className="text-xs text-[#647087] text-center py-6"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-xs text-[#647087] text-center py-6">No chats found.</div>
        ) : (
          <div className="space-y-1">
            {filtered.slice(0, 30).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => send(t)}
                disabled={busy}
                className="w-full text-left text-xs p-2 rounded border border-white/10 hover:bg-white/5 disabled:opacity-50"
                data-testid={`share-chat-thread-${t.id}`}
              >
                <div className="font-medium text-white truncate">{t.title || "Untitled chat"}</div>
                {t.subtitle && <div className="text-[10px] text-[#647087] truncate">{t.subtitle}</div>}
              </button>
            ))}
          </div>
        )}
        <div className="text-[10px] text-[#647087] mt-3 border-t border-white/5 pt-2">
          Tip: link expires {new Date(link.expires_at).toLocaleDateString()}.
        </div>
      </div>
    </div>
  );
}
